import { Grid, Skeleton } from '@mui/material';
import React, { useEffect, useRef, useState } from "react";
import { Title, usePermissions } from 'react-admin';
import { CheckChainUsesReferral, Permission, Permissions } from "./components";
import { PERMISSIONS } from './constants';
import { DashboardProvider } from "./hooks/useDashboardContext";
import { getChainId, getChainName, getPlaceId } from './lib';
import {
  AditionPointsChart,
  CommentsRating,
  DashboardAsideFilters,
  FillinDuration,
  FillingCompletelyByFuel,
  FillingCompletelyXfillingLimit,
  FillinsAverageTime,
  FillinsByFuelChart,
  FillinsByHour,
  FillinsChart,
  FuelChart,
  GeneratedXRedeemedGraph,
  NewCustomersChart,
  RankAttendants,
  RankCustomersAddedPoints,
  RefereeCount,
} from './resources/dashboard';

const LazyMount = ({ children, height = 400 }) => {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!mounted) {
    return (
      <div ref={ref}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={height}
          animation="wave"
          sx={{ borderRadius: '12px' }}
        />
      </div>
    );
  }

  return children;
};

const Dashboard = () => {
  const { permissions } = usePermissions();

  if (getChainId() || (permissions && permissions.includes(PERMISSIONS.ADMIN))) {
    return (
      <>
        <Title title={getChainName()} />
        <DashboardProvider>
          <Grid container spacing={1} sx={{ marginTop: 1 }}>
            <Grid size={{ xs: 12, lg: 2 }}>
              <Permissions permissions={[PERMISSIONS.FILLINS_AVERAGE_TIME, PERMISSIONS.FILLINS_CHART, PERMISSIONS.GENERATED_REDEEMED_POINTS, PERMISSIONS.FUEL_CHART]}>
                <DashboardAsideFilters />
              </Permissions>
            </Grid>

            <Grid size={{ xs: 12, md: 10 }}>
              <Grid container spacing={1.5} sx={{ marginBottom: 2 }}>
                <Grid size={12}>
                  <Grid container spacing={1.5}>
                    <Permission permission={PERMISSIONS.FILLINS_AVERAGE_TIME}>
                      <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
                        <FillinsAverageTime />
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.FILLIN_DURATION}>
                      <Grid size={{ xs: 12, sm: 6, md: "grow" }}>
                        <FillinDuration />
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.REFEREE_COUNT}>
                      <CheckChainUsesReferral>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <RefereeCount />
                        </Grid>
                      </CheckChainUsesReferral>
                    </Permission>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, lg: 8 }}>
                  <Grid container spacing={1.5}>
                    <Permission permission={PERMISSIONS.FILLINS_CHART}>
                      <Grid size={12}>
                        <LazyMount>
                          <FillinsChart />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.FILLINS_BY_HOUR}>
                      <Grid size={12}>
                        <LazyMount>
                          <FillinsByHour />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.FUEL_CHART}>
                      <Grid size={12}>
                        <LazyMount>
                          <FuelChart />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    {!getPlaceId() &&
                      <Permission permission={PERMISSIONS.NEW_CUSTOMERS_CHART}>
                        <Grid size={12}>
                          <LazyMount>
                            <NewCustomersChart />
                          </LazyMount>
                        </Grid>
                      </Permission>
                    }
                    <Permission permission={PERMISSIONS.FILLINS_CHART}>
                      <Grid size={12}>
                        <LazyMount>
                          <FillingCompletelyXfillingLimit />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.FILLINS_CHART}>
                      <Grid size={12}>
                        <LazyMount>
                          <FillingCompletelyByFuel />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    <Permission permission={PERMISSIONS.WORD_CLOUD}>
                      <Grid size={12}>
                        <LazyMount>
                          <CommentsRating />
                        </LazyMount>
                      </Grid>
                    </Permission>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                  <Grid container spacing={1.5}>
                    {!getPlaceId() &&
                      <Permission permission={PERMISSIONS.GENERATED_REDEEMED_POINTS}>
                        <Grid size={{ xs: 6, lg: 12 }}>
                          <LazyMount>
                            <RankCustomersAddedPoints />
                          </LazyMount>
                        </Grid>
                      </Permission>
                    }
                    <Permission permission={PERMISSIONS.FILLINS_CHART}>
                      <Grid size={{ xs: 6, lg: 12 }}>
                        <LazyMount>
                          <FillinsByFuelChart />
                        </LazyMount>
                      </Grid>
                    </Permission>
                    {!getPlaceId() &&
                      <Permission permission={PERMISSIONS.ADITION_POINTS}>
                        <Grid size={{ xs: 6, lg: 12 }}>
                          <LazyMount>
                            <AditionPointsChart />
                          </LazyMount>
                        </Grid>
                      </Permission>
                    }
                    {!getPlaceId() &&
                      <Permission permission={PERMISSIONS.GENERATED_REDEEMED_POINTS}>
                        <Grid size={{ xs: 6, lg: 12 }}>
                          <LazyMount>
                            <GeneratedXRedeemedGraph />
                          </LazyMount>
                        </Grid>
                      </Permission>
                    }
                    {!getPlaceId() &&
                      <Permission permission={PERMISSIONS.RANK_ATTENDANTS}>
                        <Grid size={{ xs: 6, lg: 12 }}>
                          <LazyMount>
                            <RankAttendants />
                          </LazyMount>
                        </Grid>
                      </Permission>
                    }
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid >
        </DashboardProvider >
      </>
    )
  }
  return <React.Fragment />;
}

export default Dashboard;
